import { useState } from 'react';
import { Link } from 'react-router';
import { ArrowUpRight } from 'lucide-react';

import { Image } from '@rocco/components/image';
import { cn } from '@rocco/utils/cn';
import {
    linkifyHtml,
    RICH_TEXT_HTML_CLASS,
} from '~/ui-rocco/pages/detail-shared';
import { ensureProtocol } from '~/modules/url';
import { getImagesSizes } from '~/utils/image-sizes-utils';

export interface ProductInfoItem {
    label: string;
    value: string;
    link?: string | null;
    newTab?: boolean;
}

export interface ProductCustomSection {
    title: string;
    html: string;
}

/**
 * Anchor id for the Product Details accordion ROW (not this content, which
 * Radix unmounts while the row is closed) — the hero's Read more links here.
 * The full-description block leads the row, so landing on the row lands on it.
 */
export const PRODUCT_DETAILS_ANCHOR_ID = 'product-details';

export interface ProductDetailsRowContentProps {
    /** Product title, leading the full-description block. */
    title?: string;
    /**
     * The FULL product description (unbounded CMS copy). Lives here at body
     * scale rather than in the hero, whose statement-size type only suits a
     * short excerpt — the hero clamps to 3 lines and anchors down to this.
     */
    descriptionHtml?: string | null;
    /**
     * Product thumbnail shown in the left column (Figma 1846:9004).
     * `isMultiplied` mirrors the hero gallery's flag: clear-cut product shots
     * sit contained on the fill and multiply into it, photos crop to fill with
     * no blend (a photo multiplied against the fill reads as a dull grey wash).
     */
    image?: { src: string; alt: string; isMultiplied?: boolean };
    /**
     * Every still the product has. When there is more than one, a small
     * thumbnail index sits under the square image and switches it. Falls back
     * to `image` alone, so callers that only have the one still are unchanged.
     */
    images?: { src: string; alt: string; isMultiplied?: boolean }[];
    /** Spec-table rows: Product / Sold by / Category / Range / Colour / … */
    items: ProductInfoItem[];
    /** Rich-text scope-of-use copy, rendered full-width under the table. */
    scopeOfUseHtml?: string | null;
    /** Custom CMS tabs (e.g. "Motion technologies", "Programme") folded in as
     *  labelled rich-text rows after the spec table. */
    customSections?: ProductCustomSection[];
}

/** Sizes for the 746px (at the 1920 anchor) square thumbnail. */
const THUMBNAIL_SIZES = getImagesSizes({ desktop: 746, mobile: 366 });
/** Small enough that the index reads as a control, not a second gallery. */
const THUMB_INDEX_SIZES = getImagesSizes({ desktop: 96, mobile: 72 });

const LABEL_CLASS =
    'shrink-0 font-sans font-medium leading-[1.3] text-black max-md:w-[7.5rem] max-md:text-sm md:w-[16.1875rem] md:text-[1rem]';
// `break-words` lets a long unbreakable value (e.g. a pasted URL in a CMS facet)
// wrap inside its cell instead of forcing the page to scroll horizontally.
// Text track + fixed arrow track so unlinked rows align with text, not icons.
const VALUE_CLASS =
    'm-0 grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_0.85em] items-baseline gap-1 break-words text-right font-sans font-normal leading-[1.3] text-[#767676] max-md:text-sm md:text-[1rem]';
const VALUE_LINE_CLASS =
    'grid w-full grid-cols-[minmax(0,1fr)_0.85em] items-baseline gap-1';
const LINK_CLASS =
    'underline decoration-black/20 underline-offset-2 transition-colors hover:text-[#1f1f21]';

/**
 * At-rest link affordance for spec-table values — the grey underline alone is
 * too subtle against the grey value text, so linked values also carry a small
 * arrow (per BC-4705 QA feedback). Kept in a fixed column so rows without a
 * link still line up with the text above.
 */
const LinkIndicatorIcon = ({ show }: { show: boolean }) => (
    <span
        className="inline-flex size-[0.85em] shrink-0 items-center justify-center"
        aria-hidden={!show}
    >
        {show ? (
            <ArrowUpRight
                aria-hidden="true"
                strokeWidth={2}
                className="size-[0.85em] -translate-y-[0.05em]"
            />
        ) : null}
    </span>
);

/** A value that is a bare URL (e.g. a Care-guide link pasted into a CMS facet) —
 *  rendered as a clickable external link rather than plain text. Matches `www.`
 *  too (href gets a protocol via `ensureProtocol`), mirroring `linkifyHtml`. */
const isExternalUrl = (value: string) =>
    /^(?:https?:\/\/|www\.)\S+$/i.test(value.trim());
/** Left-aligned rich-text for the folded-in custom sections + scope of use. */
const CUSTOM_HTML_CLASS = cn(
    // Takes the product description's ink, size and leading, but runs as one
    // full-width column. The description splits into newspaper columns because
    // it is one long unbroken read; a custom section is a short titled block,
    // and splitting those left ragged half-empty columns down the row.
    'm-0 w-full min-w-0 flex-1 font-sans text-base leading-[1.5] font-normal text-[#181818]',
    RICH_TEXT_HTML_CLASS,
    // The shared rich-text headings carry their own larger sizes; inside the
    // spec table every line sits at the row size, so they are pulled back here
    // rather than in the shared class, which other surfaces still want.
    '[&_h2]:text-[1rem] [&_h3]:text-[1rem] [&_h4]:text-[1rem]',
    '[&_table]:w-full [&_td]:py-1',
);
/**
 * A custom section can be genuine prose (Scope of use, "Motion technologies")
 * or a short single-line value — e.g. a CMS "Find a retailer" tab that is just
 * a "View Locations" link. The latter reads as a spec value, so it is
 * right-aligned like the spec rows above it rather than flowing left like a
 * paragraph. Prose (multiple/long blocks) stays left-aligned.
 */
const COMPACT_HTML_CLASS = cn(
    CUSTOM_HTML_CLASS,
    'text-right',
    // A right-aligned value cannot carry bullets: the marker sits at the
    // left of the column, an entire gap away from the text it belongs to.
    '[&_ol]:my-0 [&_ol]:list-none [&_ol]:pl-0 [&_ul]:my-0 [&_ul]:list-none [&_ul]:pl-0',
);
/** Visible text of a fragment, for comparing a heading against a label. */
const blockText = (html: string): string =>
    html
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/\s+/g, ' ')
        .trim();

/**
 * CMS authors habitually open a custom section with its own title — a bold
 * paragraph or heading repeating the tab name. The row already renders that
 * title as the section label, so the copy would say it twice. Drops the
 * leading block when it is the title and nothing else; anything that merely
 * starts with the title is left alone, as is a section whose entire body is
 * the title (removing it would leave an empty row).
 */
const stripLeadingTitle = (html: string, title: string): string => {
    const leading = html.match(/^\s*<(p|h[1-6])\b[^>]*>([\s\S]*?)<\/\1>\s*/i);
    if (!leading) return html;
    const headingText = blockText(leading[2] ?? '');
    if (headingText.toLowerCase() !== blockText(title).toLowerCase()) {
        return html;
    }
    const remainder = html.slice(leading[0].length);
    return blockText(remainder) ? remainder : html;
};

const isCompactCustomHtml = (html: string): boolean => {
    const text = html.replace(/<[^>]*>/g, '').trim();
    const hasBlockStructure = /<(?:ul|ol|h[1-6]|table|blockquote|br)\b/i.test(
        html,
    );
    const paragraphCount = (html.match(/<p\b/gi) ?? []).length;
    return !hasBlockStructure && paragraphCount <= 1 && text.length <= 60;
};

const ROW_CLASS =
    'flex items-start border-b border-black/10 py-[0.8125rem] first:pt-0 last:border-b-0 max-md:gap-4 max-md:py-2 md:gap-[3.0625rem]';
/**
 * Prose sections stack instead of sitting in the label/value pair: the title
 * on its own line with the copy full width beneath it, so it reads like the
 * description at the top of the row rather than being squeezed into the narrow
 * value column — where the newspaper flow would fold it into two thin columns.
 */
const STACKED_ROW_CLASS =
    'flex flex-col items-start gap-3 border-b border-black/10 py-[0.8125rem] first:pt-0 last:border-b-0 max-md:py-2';
const STACKED_LABEL_CLASS = cn(LABEL_CLASS, 'md:w-full');

/**
 * (01) Product Details — square product thumbnail on the left; the right
 * column leads with the product title + full description (the title stands in
 * for a "Product" spec row; on wide screens the description flows in
 * newspaper columns like the category pages' bottom text), then the
 * right-aligned spec table (label in medium ink, value in grey), hairline
 * under every row. The scope-of-use copy and the product's custom CMS tabs
 * (e.g. "Motion technologies", "Programme") fold in as labelled rich-text
 * rows after the spec table rather than rendering separately / as their own
 * numbered accordion rows.
 */
export const ProductDetailsRowContent = ({
    title,
    descriptionHtml,
    image,
    images,
    items,
    scopeOfUseHtml,
    customSections = [],
}: ProductDetailsRowContentProps) => {
    // The index drives the square image. Falls back to the single `image` so
    // this behaves exactly as before for callers without a list.
    const gallery = images?.length ? images : image ? [image] : [];
    const [selectedIndex, setSelectedIndex] = useState(0);
    /**
     * The index whose full-size image has been asked for. Picking a thumbnail
     * swaps the square to a source the browser has never fetched, so the click
     * paid for a cold request every time. Pointing at a thumbnail warms that
     * exact source first — by the time the click lands it is in cache.
     *
     * One at a time, not an accumulating set: sweeping the pointer along the
     * strip crosses every thumbnail on the way to the one wanted, and keeping
     * them all would mount a hidden full-size image per thumbnail passed over.
     * Dropping the previous one costs nothing — the request it made is already
     * in the browser's cache, which is the thing being primed.
     */
    const [warmedIndex, setWarmedIndex] = useState<number | null>(null);

    // This row keeps its position in the accordion across product navigations,
    // so it is not remounted and the selection would otherwise carry over —
    // product B opening on product A's thumbnail.
    const galleryKey = gallery.map((candidate) => candidate.src).join('|');
    const [renderedGalleryKey, setRenderedGalleryKey] = useState(galleryKey);
    if (renderedGalleryKey !== galleryKey) {
        setRenderedGalleryKey(galleryKey);
        setSelectedIndex(0);
        setWarmedIndex(null);
    }

    const shownImage = gallery[selectedIndex] ?? image;

    return (
        <div className="pb-[1.8rem] max-md:pb-4">
            <div
                className={cn(
                    'grid max-md:gap-y-6',
                    shownImage
                        ? 'md:grid-cols-[46.625rem_1fr] md:gap-x-[14.6875rem]'
                        : 'md:grid-cols-1',
                )}
            >
                {shownImage ? (
                    // Sticky on desktop so it follows the long spec/custom content
                    // instead of leaving a tall whitespace gap on the left. (The
                    // Product Details accordion row opts its content out of
                    // overflow-clip while open so this can stick — see
                    // use-product-accordion-rows.)
                    // Mobile hides the whole column — it would repeat the hero the
                    // reader just scrolled past, so the row runs text-only there.
                    <div className="hidden w-full self-start md:sticky md:top-[3.2rem] md:block">
                        {/* The product tile's fill. Whatever shows between two
                            images while the next decodes should be the same
                            quiet grey the tiles use, not a mid-tone that reads
                            as a flash of colour. */}
                        <div className="relative aspect-square w-full overflow-hidden rounded-tile bg-[var(--color-clearcut-fill)]">
                            {/* Same treatment as the hero gallery slides: clear-cut
                            images (IsMultiplied) sit contained on the fill and
                            multiply into it; photographs keep the full-bleed crop
                            and no blend mode. */}
                            <Image
                                src={shownImage.src}
                                alt={shownImage.alt}
                                fill
                                fit={
                                    shownImage.isMultiplied
                                        ? 'contain'
                                        : 'cover'
                                }
                                multiplied={Boolean(shownImage.isMultiplied)}
                                sizes={THUMBNAIL_SIZES}
                                fadeIn={false}
                            />

                            {/* Warmed sources, fetched at the square's own
                                size so the click reuses the exact candidate
                                the visible image will ask for. Behind the
                                shown one and inert — it is a cache primer,
                                not a second picture. */}
                            {(() => {
                                const candidate =
                                    warmedIndex !== null &&
                                    warmedIndex !== selectedIndex
                                        ? gallery[warmedIndex]
                                        : undefined;
                                if (!candidate) return null;
                                return (
                                    <span
                                        aria-hidden="true"
                                        className="pointer-events-none invisible absolute inset-0 -z-10"
                                    >
                                        <Image
                                            src={candidate.src}
                                            alt=""
                                            fill
                                            fit={
                                                candidate.isMultiplied
                                                    ? 'contain'
                                                    : 'cover'
                                            }
                                            multiplied={Boolean(
                                                candidate.isMultiplied,
                                            )}
                                            sizes={THUMBNAIL_SIZES}
                                            fadeIn={false}
                                        />
                                    </span>
                                );
                            })()}
                        </div>

                        {/* Thumbnail index for the square above. Only earns its
                        place when there is more than one still to choose. */}
                        {gallery.length > 1 ? (
                            <div className="mt-3 flex flex-wrap gap-2">
                                {gallery.map((candidate, index) => (
                                    <button
                                        key={`${candidate.src}-${index}`}
                                        type="button"
                                        onClick={() => setSelectedIndex(index)}
                                        onMouseEnter={() =>
                                            setWarmedIndex(index)
                                        }
                                        onFocus={() => setWarmedIndex(index)}
                                        aria-label={`Show image ${index + 1}`}
                                        aria-current={index === selectedIndex}
                                        className={cn(
                                            'relative size-[3.75rem] shrink-0 cursor-pointer overflow-hidden rounded-tile bg-[var(--color-clearcut-fill)] transition-opacity',
                                            // The shown one is stated with a ring;
                                            // the rest sit back so the strip stays
                                            // quieter than the image it controls.
                                            index === selectedIndex
                                                ? 'ring-2 ring-[#323232] ring-offset-2'
                                                : 'opacity-60 hover:opacity-100',
                                        )}
                                    >
                                        <Image
                                            src={candidate.src}
                                            alt=""
                                            aria-hidden="true"
                                            fill
                                            fit={
                                                candidate.isMultiplied
                                                    ? 'contain'
                                                    : 'cover'
                                            }
                                            multiplied={Boolean(
                                                candidate.isMultiplied,
                                            )}
                                            sizes={THUMB_INDEX_SIZES}
                                            loading="lazy"
                                            fadeIn={false}
                                        />
                                    </button>
                                ))}
                            </div>
                        ) : null}
                    </div>
                ) : null}

                {/* Right column: title + description lead (the title stands in
                for the old "Product" spec row), spec table follows. */}
                <div className="self-start">
                    {title ? (
                        <h3 className="m-0 font-sans leading-[1.25] font-medium tracking-[-0.025em] text-[#181818] max-md:text-xl md:text-[2.0625rem]">
                            {title}
                        </h3>
                    ) : null}
                    {descriptionHtml ? (
                        <div
                            className={cn(
                                // The (unbounded, user-authored) description flows
                                // newspaper-style, echoing the category pages'
                                // bottom text. The image column is fixed-width, so
                                // this column's width tracks the viewport — two
                                // text columns only fit from ~1720px up.
                                'columns-1 gap-10 font-sans text-base leading-[1.5] font-normal text-[#181818] max-md:mt-3 min-[1720px]:columns-2 md:mt-[1.5rem]',
                                // Whole paragraphs / headings / bullets move between
                                // columns rather than shearing mid-block.
                                '[&_h2]:break-inside-avoid [&_h3]:break-inside-avoid [&_h4]:break-inside-avoid [&_li]:break-inside-avoid [&_p]:break-inside-avoid',
                                RICH_TEXT_HTML_CLASS,
                            )}
                            dangerouslySetInnerHTML={{
                                __html: linkifyHtml(descriptionHtml),
                            }}
                        />
                    ) : null}
                    <dl
                        className={cn(
                            'm-0',
                            (title || descriptionHtml) &&
                                'max-md:mt-8 md:mt-[3.75rem]',
                        )}
                    >
                        {items.map((item) => (
                            <div key={item.label} className={ROW_CLASS}>
                                <dt className={LABEL_CLASS}>{item.label}</dt>
                                <dd className={VALUE_CLASS}>
                                    {item.link ? (
                                        <Link
                                            to={item.link}
                                            target={
                                                item.newTab
                                                    ? '_blank'
                                                    : undefined
                                            }
                                            rel={
                                                item.newTab
                                                    ? 'noopener noreferrer'
                                                    : undefined
                                            }
                                            className={cn(
                                                LINK_CLASS,
                                                'col-span-2',
                                                VALUE_LINE_CLASS,
                                            )}
                                        >
                                            <span className="min-w-0 text-right">
                                                {item.value}
                                            </span>
                                            <LinkIndicatorIcon show />
                                        </Link>
                                    ) : isExternalUrl(item.value) ? (
                                        <a
                                            href={ensureProtocol(
                                                item.value.trim(),
                                            )}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={cn(
                                                LINK_CLASS,
                                                'col-span-2',
                                                VALUE_LINE_CLASS,
                                            )}
                                        >
                                            <span className="min-w-0 text-right">
                                                {item.value}
                                            </span>
                                            <LinkIndicatorIcon show />
                                        </a>
                                    ) : (
                                        <>
                                            <span className="min-w-0 text-right">
                                                {item.value}
                                            </span>
                                            <LinkIndicatorIcon show={false} />
                                        </>
                                    )}
                                </dd>
                            </div>
                        ))}
                        {scopeOfUseHtml ? (
                            <div className={STACKED_ROW_CLASS}>
                                <dt className={STACKED_LABEL_CLASS}>
                                    Scope of use
                                </dt>
                                <dd
                                    className={CUSTOM_HTML_CLASS}
                                    dangerouslySetInnerHTML={{
                                        __html: linkifyHtml(scopeOfUseHtml),
                                    }}
                                />
                            </div>
                        ) : null}
                        {customSections.map((section) => {
                            // A one-line value ("View Locations") is a spec value
                            // and keeps the right-aligned pair; real prose stacks.
                            const html = stripLeadingTitle(
                                section.html,
                                section.title,
                            );
                            const isCompact = isCompactCustomHtml(html);
                            return (
                                <div
                                    key={section.title}
                                    className={
                                        isCompact
                                            ? ROW_CLASS
                                            : STACKED_ROW_CLASS
                                    }
                                >
                                    <dt
                                        className={
                                            isCompact
                                                ? LABEL_CLASS
                                                : STACKED_LABEL_CLASS
                                        }
                                    >
                                        {section.title}
                                    </dt>
                                    <dd
                                        className={
                                            isCompact
                                                ? COMPACT_HTML_CLASS
                                                : CUSTOM_HTML_CLASS
                                        }
                                        dangerouslySetInnerHTML={{
                                            __html: linkifyHtml(html),
                                        }}
                                    />
                                </div>
                            );
                        })}
                    </dl>
                </div>
            </div>
        </div>
    );
};
